import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Phone, Globe } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { language, toggleLanguage, t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();

  const scrollToSection = (id) => {
    setIsOpen(false);
    if (location.pathname !== "/") {
      navigate(`/#${id}`);
      return;
    }
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white shadow-sm" data-testid="navigation">
      <div className="px-4 md:px-8 lg:px-16">
        <div className="flex items-center justify-between h-20 md:h-20">
          {/* Logo - Shield icon with company name text */}
          <div className="flex items-center gap-2 md:gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_4c5b4c59-adba-4a3b-bcab-ae105abc8417/artifacts/36mrxgy3_ChatGPT%20Image%2016%20ene%202026%2C%2002_31_55%20p.m..png" 
              alt="Pellon Morel Solutions"
              className="h-14 md:h-16 w-auto object-contain"
            />
            <div className="flex flex-col items-center justify-center">
              <p className="text-base md:text-lg font-bold text-[#0F172A] leading-none tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
                Pellon Morel
              </p>
              <div className="h-[1.5px] bg-[#D4AF37] w-full my-[2px]"></div>
              <p className="text-xs md:text-sm font-semibold text-[#0F172A] leading-none tracking-wide" style={{ fontFamily: "'Playfair Display', serif" }}>
                Solutions
              </p>
              <p className="text-[8px] md:text-[9px] text-[#D4AF37] uppercase tracking-wider font-medium leading-none mt-[1px]">
                Plumbing-Drain-Repairs
              </p>
            </div>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-6">
            <button onClick={() => scrollToSection("inicio")} className="text-xs text-[#0F172A] hover:text-[#D4AF37] transition-colors uppercase tracking-widest" data-testid="nav-inicio">{t.nav.home}</button>
            <button onClick={() => scrollToSection("servicios")} className="text-xs text-[#0F172A] hover:text-[#D4AF37] transition-colors uppercase tracking-widest" data-testid="nav-servicios">{t.nav.services}</button>
            <button onClick={() => scrollToSection("areas")} className="text-xs text-[#0F172A] hover:text-[#D4AF37] transition-colors uppercase tracking-widest" data-testid="nav-areas">{t.nav.areas}</button>
            <button onClick={() => scrollToSection("galeria")} className="text-xs text-[#0F172A] hover:text-[#D4AF37] transition-colors uppercase tracking-widest" data-testid="nav-galeria">{t.nav.gallery}</button>
            <button onClick={() => scrollToSection("citas")} className="text-xs text-[#0F172A] hover:text-[#D4AF37] transition-colors uppercase tracking-widest" data-testid="nav-citas">{t.nav.appointment}</button>
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-1 text-[10px] text-slate-400 hover:text-[#D4AF37] transition-colors uppercase tracking-wide opacity-70 hover:opacity-100"
              data-testid="nav-language-toggle"
              title={language === 'en' ? 'Cambiar a Español' : 'Switch to English'}
            >
              <Globe className="w-3 h-3" />
              <span>{language === 'en' ? 'ES' : 'EN'}</span>
            </button>
            <a href="tel:+17576503192" className="flex items-center gap-2 bg-[#D4AF37] text-white px-5 py-2.5 text-xs uppercase tracking-widest hover:bg-[#B5952F] transition-all duration-300" data-testid="nav-call-button">
              <Phone className="w-3.5 h-3.5" />
              <span>{t.nav.callNow}</span>
            </a>
          </div>

          {/* Mobile menu button */}
          <div className="flex items-center gap-2 lg:hidden">
            <button
              onClick={toggleLanguage}
              className="w-6 h-6 flex items-center justify-center text-slate-400 hover:text-[#D4AF37] transition-colors opacity-70"
              data-testid="nav-mobile-language"
              title={language === 'en' ? 'Cambiar a Español' : 'Switch to English'}
            >
              <Globe className="w-3.5 h-3.5" />
            </button>
            <a href="tel:+17576503192" className="w-8 h-8 bg-[#D4AF37] text-white rounded-full flex items-center justify-center">
              <Phone className="w-4 h-4" />
            </a>
            <button onClick={() => setIsOpen(!isOpen)} className="text-[#0F172A] p-1" data-testid="nav-mobile-toggle">
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="lg:hidden bg-white border-t border-slate-100 shadow-lg" data-testid="nav-mobile-menu">
          <div className="px-4 py-3 flex flex-col gap-1">
            <button onClick={() => scrollToSection("inicio")} className="text-left text-sm text-[#0F172A] hover:text-[#D4AF37] hover:bg-slate-50 transition-colors uppercase tracking-widest py-3 px-2 rounded" data-testid="nav-mobile-inicio">{t.nav.home}</button>
            <button onClick={() => scrollToSection("servicios")} className="text-left text-sm text-[#0F172A] hover:text-[#D4AF37] hover:bg-slate-50 transition-colors uppercase tracking-widest py-3 px-2 rounded" data-testid="nav-mobile-servicios">{t.nav.services}</button>
            <button onClick={() => scrollToSection("areas")} className="text-left text-sm text-[#0F172A] hover:text-[#D4AF37] hover:bg-slate-50 transition-colors uppercase tracking-widest py-3 px-2 rounded" data-testid="nav-mobile-areas">{t.nav.areas}</button>
            <button onClick={() => scrollToSection("galeria")} className="text-left text-sm text-[#0F172A] hover:text-[#D4AF37] hover:bg-slate-50 transition-colors uppercase tracking-widest py-3 px-2 rounded" data-testid="nav-mobile-galeria">{t.nav.gallery}</button>
            <button onClick={() => scrollToSection("citas")} className="text-left text-sm text-[#0F172A] hover:text-[#D4AF37] hover:bg-slate-50 transition-colors uppercase tracking-widest py-3 px-2 rounded" data-testid="nav-mobile-citas">{t.nav.appointment}</button>
            <a href="tel:+17576503192" className="flex items-center justify-center gap-2 bg-[#D4AF37] text-white px-5 py-3 text-sm uppercase tracking-widest hover:bg-[#B5952F] transition-all duration-300 mt-2 rounded" data-testid="nav-mobile-call-button">
              <Phone className="w-4 h-4" />
              <span>{t.nav.callNow}</span>
            </a>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navigation;